import { EventEmitter } from '@angular/core';
import { User } from './classes/user.class';

export interface ExecutorPlanningSettings {
    viewOptions: string[];
}

class StorageInterface {
    public static user: User;
}

export class LocalStorage implements StorageInterface {
    public static customerAreaChange: EventEmitter<any> = new EventEmitter();

    public static get user(): User {
        return this.getUser();
    }

    public static set user(user: User) {
        this.setUser(user);
    }

    public static setUserToken(token: string) {
        localStorage.setItem('access_token', token);
    }

    public static setUser(user: User) {
        localStorage.setItem('user', JSON.stringify(user));
    }

    public static getUser(): User {
        if (localStorage.getItem('user') !== 'undefined') {
            return JSON.parse(localStorage.getItem('user'));
        }
        return null;
    }

    public static logoutUser() {
        localStorage.clear();
    }

    public static getUserToken() {
        return localStorage.getItem('access_token');
    }

    public static setLastActivity(date: string) {
        localStorage.setItem('lastActivity', date);
    }

    public static getLastActivity() {
        return localStorage.getItem('lastActivity');
    }

    public static set weekplanningTypeFilter(entityTypeKeys: string[]) {
        localStorage.setItem('weekplanningTypeFilter', entityTypeKeys.join(','));
    }

    public static get weekplanningTypeFilter() {
        return localStorage
            .getItem('weekplanningTypeFilter')
            ?.split(',')
            .map(p => p)
            .filter(p => p?.length > 0);
    }

    public static set weekplanningProjectManagerFilter(projectManagerId: number | null) {
        localStorage.setItem('weekplanningProjectManagerFilter', String(projectManagerId ?? ''));
    }

    public static get weekplanningProjectManagerFilter(): number | null {
        return +localStorage.getItem('weekplanningProjectManagerFilter') || null;
    }

    public static set needOverviewOpen(show: boolean) {
        localStorage.setItem('needOverviewOpen', show ? '1' : '0');
    }

    public static get needOverviewOpen() {
        return !!+localStorage.getItem('needOverviewOpen');
    }

    public static set hourTypeFilter(functions: string[]) {
        localStorage.setItem('hourTypeFilter', functions.join(','));
    }

    public static get hourTypeFilter() {
        return localStorage
            .getItem('hourTypeFilter')
            ?.split(',')
            .map(p => p)
            .filter(p => p?.length > 0);
    }

    public static set userFunctionFilter(functions: string[]) {
        localStorage.setItem('userFunctionFilter', functions.join(','));
    }

    public static get userFunctionFilter() {
        return localStorage
            .getItem('userFunctionFilter')
            ?.split(',')
            .map(p => p)
            .filter(p => p?.length > 0);
    }

    public static set PlanningFilter(ids: string[]) {
        localStorage.setItem('planningFilter', ids.join(','));
    }

    public static get PlanningFilter() {
        return localStorage
            .getItem('planningFilter')
            ?.split(',')
            .filter(p => p?.length > 0);
    }

    public static set ShowInUse(show: boolean) {
        localStorage.setItem('showInUse', show ? '1' : '0');
    }

    public static get ShowInUse() {
        return !!+localStorage.getItem('showInUse');
    }

    public static set ShowComments(show: boolean) {
        localStorage.setItem('showComments', show ? '1' : '0');
    }

    public static get ShowComments() {
        return !!+localStorage.getItem('showComments');
    }

    public static set ShowSmallMaterials(show: boolean) {
        localStorage.setItem('showSmallMaterials', show ? '1' : '0');
    }

    public static get ShowSmallMaterials() {
        return !!+localStorage.getItem('showSmallMaterials');
    }

    public static saveSettings(settings: object) {
        localStorage.setItem('settings', JSON.stringify(settings));
    }

    public static getSettings() {
        const settings = JSON.parse(localStorage.getItem('settings'));
        return settings ? settings : {};
    }

    public static set executorPlanningSettings(settings: ExecutorPlanningSettings) {
        localStorage.setItem('executorPlanningSettings', JSON.stringify(settings));
    }

    public static get executorPlanningSettings(): ExecutorPlanningSettings {
        const raw = localStorage.getItem('executorPlanningSettings');
        return raw ? JSON.parse(raw) : { viewOptions: ['hideUnplanned'] };
    }

    public static websocketRetryIncrement() {
        let cntr = +localStorage.getItem('websocketRetry');
        cntr = cntr ? +cntr : 1;
        cntr++;
        localStorage.setItem('websocketRetry', `${cntr}`);
        return cntr;
    }

    public static websocketResetRetry() {
        localStorage.setItem('websocketRetry', '0');
    }

    public static setExpireTimeToken(token: number) {
        localStorage.setItem('token_date', new Date().toISOString());
        localStorage.setItem('expire_access_token', token.toString());
    }

    public static getExpireTimeToken() {
        return localStorage.getItem('expire_access_token');
    }

    public static getMail(): string {
        return localStorage.getItem('mail') || '';
    }

    public static setMail(mail: string) {
        localStorage.setItem('mail', mail);
    }

    public static getTokenDate() {
        return localStorage.getItem('token_date');
    }

    public static unsetParentUser() {
        localStorage.removeItem('parent_access_token');
        localStorage.removeItem('parent_user');
    }

    public static setParentUser(token: string, user: User) {
        localStorage.setItem('parent_access_token', token);
        localStorage.setItem('parent_user', JSON.stringify(user));
    }

    public static getParentUserToken() {
        return localStorage.getItem('parent_access_token');
    }

    public static get parentUser(): User {
        return JSON.parse(localStorage.getItem('parent_user'));
    }
}
